import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Order } from '../types';
import './OrdersPage.css';

export const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading orders...');
      const response = await ordersApi.getOrders();
      console.log('Orders response:', response);

      if (response && Array.isArray(response.orders)) {
        setOrders(response.orders);
        console.log(`Loaded ${response.orders.length} orders`);
      } else {
        console.warn('Unexpected response format:', response);
        setOrders([]);
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    loadOrders();

    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  }, [tg, loadOrders, handleBack]);

  const handleOrderClick = (orderId: number) => {
    navigate(`/orders/${orderId}`);
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'Ожидает оплаты',
      paid: 'Оплачен',
      preparing: 'Собирается',
      delivering: 'В доставке',
      completed: 'Завершен',
      cancelled: 'Отменен',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      pending: 'status-pending',
      paid: 'status-paid',
      preparing: 'status-preparing',
      delivering: 'status-delivering',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return colorMap[status] || '';
  };

  if (loading) {
    return (
      <div className="page orders-page">
        <div className="orders-header">
          <button className="back-btn" onClick={handleBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="orders-title">Мои заказы</h1>
          <a
            href="https://t.me/TESTORIA_Mosk"
            target="_blank"
            rel="noopener noreferrer"
            className="header-manager-btn"
          >
            💬 Менеджер
          </a>
        </div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page orders-page">
        <div className="orders-header">
          <button className="back-btn" onClick={handleBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="orders-title">Мои заказы</h1>
          <a
            href="https://t.me/TESTORIA_Mosk"
            target="_blank"
            rel="noopener noreferrer"
            className="header-manager-btn"
          >
            💬 Менеджер
          </a>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-button" onClick={loadOrders}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const isEmpty = orders.length === 0;

  return (
    <div className="page orders-page">
      <div className="orders-header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="orders-title">Мои заказы</h1>
        <a
          href="https://t.me/TESTORIA_Mosk"
          target="_blank"
          rel="noopener noreferrer"
          className="header-manager-btn"
        >
          💬 Менеджер
        </a>
      </div>

      {isEmpty ? (
        <div className="empty-orders">
          <div className="empty-orders-icon">
            📦
          </div>
          <h2 className="empty-orders-title">У вас пока нет заказов</h2>
          <p className="empty-orders-text">Оформите первый заказ из каталога</p>
          <button className="back-to-catalog-btn" onClick={handleBack}>
            Перейти в каталог
          </button>
        </div>
      ) : (
        <div className="orders-content">
          <div className="orders-list">
            {orders.map((order) => (
              <div
                key={order.id}
                className="order-card"
                onClick={() => handleOrderClick(order.id)}
              >
                <div className="order-card-header">
                  <div className="order-number">Заказ #{order.id}</div>
                  <div className={`order-status ${getStatusColor(order.status)}`}>
                    {getStatusLabel(order.status)}
                  </div>
                </div>

                <div className="order-card-date">
                  {formatDate(order.created_at)}
                </div>

                {order.items && order.items.length > 0 && (
                  <div className="order-card-items">
                    {order.items.slice(0, 3).map((item, index) => (
                      <div key={index} className="order-card-item">
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={item.product_name}
                            className="order-item-image"
                            loading="eager"
                            onError={(e) => {
                              e.currentTarget.src = 'https://via.placeholder.com/32x32?text=No+Image';
                            }}
                          />
                        )}
                        <div className="order-item-info">
                          <span className="order-item-name">{item.product_name}</span>
                          <span className="order-item-quantity">×{item.quantity}</span>
                        </div>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="order-card-more">
                        +{order.items.length - 3} товара
                      </div>
                    )}
                  </div>
                )}

                <div className="order-card-footer">
                  <span className="order-total-label">Итого:</span>
                  <span className="order-total-value">
                    {formatPrice(order.total_amount + order.delivery_fee)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
